import { userFacingError } from "../../userFacingError";
import type { AuthDescriptor } from "../authDescriptor";
import { awsOidcSteps } from "../aws/oidc";
import { customAuth, fetchTokenAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { escapeRegexLiteral } from "../urlPatternConversion";
import logo from "./logo.svg";

function registryEndpoint(value: string | undefined) {
  const endpoint = value?.trim().replace(/\/$/, "") ?? "";
  if (!endpoint) return null;
  const match =
    /^https:\/\/\d{12}\.dkr\.ecr\.([a-z]+(?:-[a-z]+)+-\d+)\.(amazonaws\.com(?:\.cn)?)$/.exec(
      endpoint
    );
  if (!match)
    throw userFacingError(
      "Enter an ECR registry URL such as https://123456789012.dkr.ecr.us-east-1.amazonaws.com"
    );
  return { endpoint, region: match[1]!, suffix: match[2]! };
}

const registrySteps = [
  {
    title: "Choose the private registry",
    items: [
      {
        kind: "baseUrl",
        label: "Registry URL",
        placeholder: "https://123456789012.dkr.ecr.us-east-1.amazonaws.com",
        required: true,
        target: { kind: "configuration", field: "registryEndpoint" },
      },
      {
        kind: "markdown",
        content:
          "Grant this identity `ecr:GetAuthorizationToken` and read access to the repositories your steps use, including `ecr:BatchGetImage`, `ecr:GetDownloadUrlForLayer`, and `ecr:BatchCheckLayerAvailability`. Attach this connector to steps that pull private ECR images.",
      },
    ],
  },
] as const;

const defaultUrlPattern = ({
  registryEndpoint: value,
}: Record<string, string>) => {
  const registry = registryEndpoint(value);
  return registry ? `${escapeRegexLiteral(registry.endpoint)}/v2/.*` : "";
};

const connectionTest = {
  protocol: "http",
  request: ({ registryEndpoint: value }: Record<string, string>) =>
    `${registryEndpoint(value)?.endpoint ?? ""}/v2/`,
} as const;

function registryAuth(value: string | undefined, auth: AuthDescriptor) {
  const registry = registryEndpoint(value);
  return fetchTokenAuth({
    request: {
      url: registry
        ? `https://api.ecr.${registry.region}.${registry.suffix}/`
        : "",
      headers: [
        { name: "Content-Type", value: "application/x-amz-json-1.1" },
        {
          name: "X-Amz-Target",
          value: "AmazonEC2ContainerRegistry_V20150921.GetAuthorizationToken",
        },
      ],
      rawBody: "{}",
      auth,
    },
    extract: {
      from: "jsonPath",
      paths: ["authorizationData.0.authorizationToken"],
    },
    apply: { kind: "header", scheme: "Basic" },
    cache: { ttlSeconds: 43_140 },
  });
}

export const awsEcr = defineProduct({
  name: "AWS ECR",
  tagLine: "Private container images on Amazon ECR",
  logo,
  tags: ["engineering"],
  variants: [
    productVariant({
      identifier: "awsEcr",
      authLabel: "OIDC (IAM role federation)",
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        registryAuth(
          app.configuration.registryEndpoint,
          customAuth("awsRole", {
            roleArn: app.config.roleArn,
            issuer: app.issuer ?? "",
          })
        ),
      steps: [...awsOidcSteps, ...registrySteps],
      connectionTest,
    }),
    productVariant({
      identifier: "awsEcrKey",
      authLabel: "Key-based (access/secret key pair)",
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        registryAuth(
          app.configuration.registryEndpoint,
          customAuth("awsSigV4", {
            accessKeyId: app.config.accessKeyId,
            secretAccessKey: app.secret.secretAccessKey,
            sessionToken: app.secret.sessionToken,
          })
        ),
      steps: [
        {
          title: "Enter your AWS credentials",
          items: [
            {
              kind: "input",
              label: "Access key ID",
              required: true,
              target: { kind: "configuration", field: "accessKeyId" },
            },
            {
              kind: "input",
              label: "Secret access key",
              required: true,
              target: { kind: "secrets", field: "secretAccessKey" },
            },
            {
              kind: "input",
              label: "Session token (optional)",
              target: { kind: "secrets", field: "sessionToken" },
            },
          ],
        },
        ...registrySteps,
      ],
      connectionTest,
    }),
  ],
});
